import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../utils/api';
import {
  findTemplateById,
  parseIdCardTemplate,
} from '../utils/idCardFormats';

/**
 * School-wide active ID card template from report_templates registry.
 */
export function useActiveIdCardTemplate(schoolId) {
  const [templates, setTemplates] = useState([]);
  const [activeTemplateId, setActiveTemplateId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    if (!schoolId) {
      setTemplates([]);
      setActiveTemplateId('');
      return null;
    }
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/reports/templates', {
        params: { category: 'id_card', school_id: schoolId },
      });
      const data = res.data?.data || {};
      const list = data.templates || [];
      const activeId = data.active_template_id || list[0]?.id || '';
      setTemplates(list);
      setActiveTemplateId(activeId);
      return data;
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load ID card templates');
      return null;
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const activeTemplate = useMemo(
    () => findTemplateById(templates, activeTemplateId) || templates[0] || null,
    [templates, activeTemplateId],
  );

  const { design, format } = useMemo(
    () => parseIdCardTemplate(activeTemplate),
    [activeTemplate],
  );

  const saveTemplate = useCallback(async (templateId) => {
    if (!schoolId || !templateId) return null;
    setSaving(true);
    setError('');
    try {
      const res = await api.put('/reports/templates/active', {
        school_id: schoolId,
        category: 'id_card',
        template_id: templateId,
      });
      const nextActive = res.data?.data?.active_template_id || templateId;
      setActiveTemplateId(nextActive);
      await refresh();
      return nextActive;
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save template');
      throw e;
    } finally {
      setSaving(false);
    }
  }, [schoolId, refresh]);

  return {
    templates,
    activeTemplateId,
    activeTemplate,
    design,
    format,
    loading,
    error,
    saving,
    refresh,
    saveTemplate,
  };
}

export default useActiveIdCardTemplate;
